import type {
  InjectionProtocol,
  ProtocolBrand,
  ProtocolWarning,
} from "../types";

/**
 * Triptorelin 0.1 mg — the daily agonist of the long protocol, and the first
 * walk here whose label says almost nothing about the hands.
 *
 * The French leaflet for the 0.1 mg kit states: one injection a day, under
 * the skin, from the second day of the cycle to the eve of the trigger; the
 * powder is dissolved immediately before injecting with the supplied solvent
 * only, and mixed with nothing else; immediate use; no double dose, contact
 * the doctor; and the allergic-reaction picture. It names no site, no needle,
 * no angle, and does not say how to open the glass ampoule the solvent comes
 * in. That is the Ovidrel finding again, on a kit rather than a syringe.
 *
 * So this walk is six steps of which the label backs the frame and the
 * conventions fill the hands: the ampoule snapped away from her, the swirl,
 * the needle change, the abdomen, the pinch. Every one of those is tier P and
 * the fiche says so, step by step, for clinical review. The alternative — a
 * walk that said "dissolve and inject under the skin" and nothing more — would
 * leave her alone with a glass ampoule, which is the one thing in this kit
 * that can cut her.
 *
 * ### One label, one language
 *
 * The daily kit is nationally authorised (Ipsen, France and a handful of
 * other EEA markets). No English label exists: the UK's Gonapeptyl Daily is
 * no longer on emc, and the Canadian monograph in `SOURCES.md` describes a
 * ready-to-use syringe this kit is not. The English copy is the author's
 * reading of the French notice, and the fiche flags it as such. The brand is
 * Decapeptyl alone: Gonapeptyl and Diphereline are other presentations with
 * leaflets this walk has not read.
 *
 * Restated from `content/data/medications/gnrh-agonists.json` (the
 * `Decapeptyl` entry), rewritten against the notice first — it carried a
 * gauge, an angle, a two-hour missed-dose rule and trigger timing the label
 * does not have.
 */

const DECAPEPTYL_BRANDS = [
  {
    name: "Decapeptyl",
    markets: ["FR", "DE", "ES", "IT", "BE", "IE", "PT"],
  },
] as const satisfies readonly ProtocolBrand[];

/** The notice's own two sentences, no hour. */
const MISSED_DOSE: ProtocolWarning = {
  id: "decapeptyl.missed-dose",
  severity: "caution",
  escalation: "none",
  copy: {
    en: "Missed dose - Whatever happens, contact your clinic. Do not take a double dose to make up for a forgotten dose.",
    fr: "Dose oubliée - Dans tous les cas, contactez votre centre. Ne prenez pas de dose double pour compenser la dose que vous avez oublié de prendre.",
  },
  sourceSegment:
    "⚠ Missed dose - Whatever happens, contact your clinic. Do not take a double dose to make up for a forgotten dose.",
};

/**
 * The notice's allergic-reaction picture, verbatim in substance, routed to
 * emergency as for every medication here.
 */
const ALLERGIC_REACTION: ProtocolWarning = {
  id: "decapeptyl.breathing-or-allergic-reaction",
  severity: "stop",
  escalation: "emergency",
  copy: {
    en: "Seek urgent help - Difficulty swallowing or breathing, dizziness, swelling of the lips, face, throat or tongue, or a rash need emergency services straight away.",
    fr: "Aide urgente - Des difficultés à avaler ou à respirer, des sensations vertigineuses, un gonflement des lèvres, du visage, de la gorge ou de la langue, ou une éruption cutanée nécessitent d'appeler les secours tout de suite.",
  },
  sourceSegment:
    "⚠ Seek urgent help - Difficulty swallowing or breathing, dizziness, swelling of the lips, face, throat or tongue, or a rash need emergency services straight away.",
};

export const DECAPEPTYL_PROTOCOL: InjectionProtocol = {
  medicationSlug: "decapeptyl",
  brands: DECAPEPTYL_BRANDS,
  aliases: ["Triptorelin", "Triptoréline", "Décapeptyl"],
  indication: "agonist",
  // 100 micrograms per vial, written "0,1 mg" on the French box. A record in
  // micrograms is asked about rather than converted — the box is the answer.
  dose: { unit: "mg", strengths: [0.1] },
  form: "vial",
  prescriptionForms: ["lyophilized_powder"],
  route: "subcutaneous",
  version: "2026-10-05.1",
  derivedFrom: "content/data/medications/gnrh-agonists.json#triptorelin",
  steps: [
    {
      // "Use only the solvent supplied" and the daily schedule are the
      // label's; the list of things to lay out is convention.
      id: "decapeptyl.vial.prepare",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Prepare - Wash your hands and lay out one vial of {brand} powder, one ampoule of the solvent supplied in the pack, a syringe, a needle for preparation, a fine needle for injection under the skin, alcohol swabs and a sharps container. Use only the solvent supplied in the pack. One injection a day, from the second day of your cycle until the day before your trigger, exactly as your clinic prescribed.",
        fr: "Préparer - Lavez-vous les mains et disposez un flacon de poudre de {brand}, une ampoule du solvant fourni dans la boîte, une seringue, une aiguille pour la préparation, une aiguille fine pour l'injection sous la peau, des compresses alcoolisées et un collecteur. N'utilisez que le solvant fourni dans la boîte. Une injection par jour, à partir du deuxième jour de votre cycle jusqu'à la veille de votre déclenchement, exactement comme votre centre l'a prescrit.",
      },
      warnings: [MISSED_DOSE],
    },
    {
      // "Just before injecting", "only the supplied solvent" and "mixed with
      // no other medicine" are the label's. The ampoule, the swirl and the
      // clear-solution check are convention — and the ampoule is the step
      // that can cut her, which is why it is said.
      id: "decapeptyl.vial.reconstitute",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Reconstitute - Just before injecting, snap the solvent ampoule open at its neck, away from you, with a swab around it. Draw the solvent into the syringe through the preparation needle, inject it slowly into the powder vial and swirl gently until the powder has dissolved; do not shake. Check that the solution is clear. Do not mix it with any other medicine.",
        fr: "Reconstituer - Juste avant l'injection, cassez l'ampoule de solvant au niveau du col, en l'éloignant de vous, avec une compresse autour. Prélevez le solvant dans la seringue avec l'aiguille de préparation, injectez-le lentement dans le flacon de poudre et faites tourner doucement jusqu'à dissolution de la poudre ; n'agitez pas. Vérifiez que la solution est limpide. Ne la mélangez à aucun autre médicament.",
      },
    },
    {
      id: "decapeptyl.vial.change-the-needle",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Change the needle - Swap the preparation needle for the fine needle. If you see air bubbles, hold the syringe with the needle pointing up, flick it gently so the bubbles rise, and push the plunger until they are gone.",
        fr: "Changer l'aiguille - Remplacez l'aiguille de préparation par l'aiguille fine. Si vous voyez des bulles d'air, tenez la seringue aiguille vers le haut, tapotez-la doucement pour faire monter les bulles et poussez le piston jusqu'à ce qu'elles aient disparu.",
      },
    },
    {
      // The label names no site at all. The abdomen is the convention every
      // subcutaneous fertility injection follows, tier P.
      id: "decapeptyl.vial.clean-site",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Clean the site - Clean a spot on your abdomen with an alcohol swab and let it dry. Choose a different spot each day.",
        fr: "Désinfecter le site - Nettoyez une zone du ventre avec une compresse alcoolisée et laissez sécher. Changez d'endroit chaque jour.",
      },
    },
    {
      // "Under the skin" is the label's; the pinch and the slow plunger are
      // convention. No angle is stated and none is drawn.
      id: "decapeptyl.vial.inject",
      kind: "irreversible",
      requiresConfirmation: true,
      copy: {
        en: "Inject - Give it under the skin: pinch a fold of skin, insert the needle into the fold and push the plunger slowly until the syringe is empty. Then withdraw the needle and press a swab on the spot without rubbing.",
        fr: "Injecter - Injectez sous la peau : pincez un pli de peau, insérez l'aiguille dans le pli et poussez le piston lentement jusqu'à ce que la seringue soit vide. Retirez ensuite l'aiguille et appuyez une compresse sur l'endroit sans frotter.",
      },
      warnings: [ALLERGIC_REACTION],
    },
    {
      // "Immediate use" and "never stop without your doctor" are the label's.
      id: "decapeptyl.vial.aftercare",
      kind: "aftercare",
      requiresConfirmation: false,
      copy: {
        en: "After injection - Use the solution immediately and do not keep any of it. Put the needles, the syringe, the vial and the ampoule in your sharps container right away. Never stop the treatment without your doctor's advice.",
        fr: "Après l'injection - Utilisez la solution immédiatement et n'en conservez rien. Mettez tout de suite les aiguilles, la seringue, le flacon et l'ampoule dans votre collecteur. N'arrêtez jamais le traitement sans l'avis de votre médecin.",
      },
    },
  ],
};
